import React, { FC } from 'react';
import { formatValue } from 'react-currency-input-field';
import { connect, ConnectedProps } from 'react-redux';
import classNames from 'classnames';
import { isEmpty } from 'lodash';

import lang from '../../lib/language';
import { RootState } from '../../lib/store';
import { LeaderboardCompetitor } from '../../lib/store/reporting/types';
import { formatCapitalTournamentPrize, formatNumber } from '../../lib/util/DataHelpers';

import FirstPlayerIcon from '../../assets/img/first_player.png';
import SecondPlayerIcon from '../../assets/img/second_player.png';
import ThirdPlayerIcon from '../../assets/img/third_player.png';
import { BaseTable, UserPhoto } from '../../components';
import NoDataAvailableImage from '../../components/NoDataAvailableImage/NoDataAvailableImage';
import { useIsMobile } from '../../hooks';

import { LeaderboardHeaders } from './Leaderboard.constants';
import SkeletonLoaderRow from './SkeletonLoaderRow';

import styles from './Leaderboard.module.scss';

type BaseTournamentProps = {
  isTournamentStarted: boolean;
  isTournamentEnded: boolean;
};

type Props = BaseTournamentProps &
  ReduxProps & {
    data: LeaderboardCompetitor[];
    isLoading: boolean;
    initialAdjustmentAmount: number;
  };

const Ranking: FC<Props> = (props: Props): JSX.Element => {
  const { data, isLoading, isTournamentEnded, isTournamentStarted, initialAdjustmentAmount } = props;
  const willTournamentStart = !isTournamentEnded && !isTournamentStarted;
  const isMobile = useIsMobile();

  const tableHeaders = LeaderboardHeaders.map((item: string, index: number) => (
    <th className={index === 0 ? styles.tableRanking : ''}>{item}</th>
  ));

  const renderList = () => {
    const rows = data.map((item: LeaderboardCompetitor): JSX.Element => {
      const { profileName, firstName, lastName, pnL, netEquity, prize, rank } = item;
      const percentProfit = netEquity ? ((netEquity - initialAdjustmentAmount) / initialAdjustmentAmount) * 100 : 0;
      const firstAndLastChar = firstName && lastName ? firstName[0] + lastName[0] : '-';

      return (
        <tr className={styles.row} key={profileName}>
          <td>
            {rank === 1 && <img src={FirstPlayerIcon} className={isMobile ? styles.indexMobFirst : ''} alt="First Player" />}
            {rank === 2 && <img src={SecondPlayerIcon} className={isMobile ? styles.indexMobFirst : ''} alt="Second Player" />}
            {rank === 3 && <img src={ThirdPlayerIcon} className={isMobile ? styles.indexMobFirst : ''} alt="Third Player" />}
            {(rank > 3) && <div className={isMobile ? styles.indexMob : styles.index}>{`#${rank}`}</div>}
          </td>
          <td className={styles.nicknameTd}>
            <div className={styles.nickname}>
              <UserPhoto customText={firstAndLastChar} isHeader />
              {isMobile ? (
                <div className={styles.text}>
                  <div>{profileName}</div>
                  {prize > 0 && (
                  <div className={styles.reward}>
                    {lang.commonPrize()}
                    {formatCapitalTournamentPrize(prize)}
                  </div>
                  )}
                </div>
              ) : (
                <span className={styles.text}>{profileName}</span>
              )}
            </div>
          </td>
          <td
            className={classNames(styles.profit, {
              [styles.positive]: pnL > 0,
              [styles.negative]: pnL < 0,
            })}
          >
            <div>{`${pnL > 0 ? '+' : ''}$${formatNumber(pnL)}`}</div>
            {isMobile && <div className={styles.percentProfit}>{`(${percentProfit > 0 ? '+' : ''}${formatNumber(percentProfit)}%)`}</div>}
          </td>
          {!isMobile && (
            <td
              className={classNames(styles.percentChange, {
                [styles.positive]: percentProfit > 0,
                [styles.negative]: percentProfit < 0,
              })}
            >
              {`${percentProfit > 0 ? '+' : ''}${formatNumber(percentProfit)}%`}
            </td>
          )}
          {!isMobile && <td className={styles.prize}>{prize > 0 ? `BGN ${formatCapitalTournamentPrize(prize)}` : '-'}</td>}
        </tr>
      );
    });

    while (rows.length < 4) {
      rows.push(<div className={styles.row} />);
    }

    return rows;
  };

  const notDataToDisplay = !isLoading && isEmpty(data);
  return (
    <>
      {willTournamentStart || notDataToDisplay ? (
        <NoDataAvailableImage description={lang.commonMyPositionFollowTournament()} />
      ) : (
        <BaseTable className={styles.table}>
          {!isMobile && (
            <thead>
              <tr>{tableHeaders}</tr>
            </thead>
          )}
          <tbody>
            {isLoading ? (
              <>
                <SkeletonLoaderRow />
                <SkeletonLoaderRow />
                <SkeletonLoaderRow />
                <SkeletonLoaderRow />
              </>
            ) : (
              <>{renderList()}</>
            )}
          </tbody>
        </BaseTable>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  leaderboard: state.reporting.leaderboard,
});

const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(Ranking);
